import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useEvent } from "../context/EventProvider";
import { createForm, newFormName } from "../funnel/formCreate";
import { useForms } from "../funnel/formQueries";
import { useSettings } from "../settings/context";
import { Button } from "../ui/Button";

/**
 * « NOUVEAU FORMULAIRE VIDE » — sous un sélecteur de formulaire.
 *
 * Un bouton, puis un nom à confirmer, et le formulaire est créé sur l'événement,
 * choisi dans le sélecteur, prêt pour l'éditeur de champs. Voir `funnel/formCreate.ts`
 * pour ce qui est créé et pourquoi jamais au niveau du domaine.
 *
 * ⚠️ LE NOM SE DEMANDE, IL NE SE DEVINE PAS. « Nouveau formulaire 3 » dans une liste de
 * six devient introuvable au back office la semaine suivante ; le champ est pré-rempli
 * pour que confirmer coûte une touche, pas plus.
 *
 * Displayed strings are French: the organiser is a French speaker.
 */
export function NewForm({
  type,
  onCreated,
}: {
  /** Participant (`U`) ou société (`C`) : celui que le sélecteur attend. */
  type: "U" | "C";
  /** Le formulaire créé, à choisir dans le sélecteur. */
  onCreated: (id: number) => void;
}) {
  const event = useEvent();
  const client = useQueryClient();
  const { saving } = useSettings();
  const forms = useForms();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const start = () => {
    setName(newFormName(forms.filter((f) => f.type === type).map((f) => f.name)));
    setFailure(null);
    setOpen(true);
  };

  const create = async () => {
    if (!event || busy || name.trim() === "") return;
    setBusy(true);
    setFailure(null);
    try {
      const id = await createForm(event.domainId, event.eventId, { name, type });
      /* La liste qui nourrit le sélecteur : sans ça, l'identifiant écrit pointerait
         vers un formulaire que le panneau ne connaît pas encore (« Hors liste »). */
      await client.invalidateQueries({
        queryKey: ["forms", event.domainId, event.eventId],
      });
      onCreated(id);
      setOpen(false);
    } catch (cause) {
      setFailure(
        cause instanceof Error && cause.message
          ? cause.message
          : "Le formulaire n'a pas pu être créé.",
      );
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        className="nwf__o"
        disabled={!saving || !event}
        onClick={start}
      >
        + Nouveau formulaire vide
      </button>
    );
  }

  return (
    <div className="nwf">
      <input
        type="text"
        value={name}
        autoFocus
        aria-label="Nom du formulaire"
        placeholder="Nom du formulaire"
        disabled={busy}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") void create();
          if (e.key === "Escape" && !busy) setOpen(false);
        }}
      />
      <div className="nwf__a">
        <Button
          type="button"
          variant="ghost"
          disabled={busy}
          onClick={() => setOpen(false)}
        >
          Annuler
        </Button>
        <Button
          type="button"
          loading={busy}
          disabled={name.trim() === ""}
          onClick={() => void create()}
        >
          Créer
        </Button>
      </div>
      {failure && (
        <p className="fed__err" role="alert">
          {failure}
        </p>
      )}
    </div>
  );
}
